import { ComingSoonTag, EarlyAccessTag, NewTag } from '../tag';
import { TextButton } from '../buttons';
import React, { FC } from 'react';
import { SkeletonBlock, SkeletonRow, SkeletonStack } from '../skeleton';
import { useNavigate } from 'react-router-dom';
import { T } from 'src/shared/helpers';
import usePermissions from 'src/shared/hooks/usePermissions';
import styled from 'styled-components';
import { usePantoneColorsCheck } from '../../../shared/hooks/usePantoneColorsCheck';
import { useInlineHelp } from '../inlineHelpLayout';

export interface PageBlock {
    isNew?: boolean;
    isComingSoon?: boolean;
    isEarlyAccess?: boolean;
    label: string;
    description: string;
    learnMore?: string;
    buttonLabel: string;
    buttonAction?: () => void;
    link?: string;
    permissions: [];
}

export interface PageBlocksListProps {
    blocks: PageBlock[];
    columns?: number;
}

interface PageBlocksListSkeletonProps {
    count?: number;
    columns?: number;
    showLearnMore?: boolean;
}

const PageBlocksContainer = styled.ul<{ $columns: number }>`
    display: grid;
    gap: 20px;
    grid-template-columns: repeat(${(props) => props.$columns}, minmax(0, 1fr));
    grid-auto-rows: 1fr;
    list-style: none;
    padding: 0;
    p {
        display: flex;
        flex: 1;
        overflow: hidden;
    }
    margin-top: 0px;
`;

const PageBlock = styled.li`
    background-color: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.backgroundColor};
    border-radius: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.borderRadius};
    border: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.border};
    padding: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.padding};
    display: flex;
    flex-direction: column;
    align-items: stretch;
    justify-content: flex-start;
    height: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.height};
    transition: box-shadow 0.2s ease;
    &:hover {
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
    }
`;

const PageBlockTitle = styled.span`
    color: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockTitle.color};
    display: flex;
    align-items: flex-start;
    gap: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockTitle.gap};
    font-size: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockTitle.fontSize};
    font-weight: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockTitle.fontWeight};
    font-family: 'Inter', sans-serif;
`;

const PageBlockDescription = styled.p`
    flex: 1;
    font-size: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockDescription.fontSize};
    line-height: 16px;
    color: ${(props) => props.theme.app.textColor};
`;

const PageBlockFooter = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
`;

const StyledTextButton = styled(TextButton)<{ $disabled?: boolean }>`
    font-size: 13px;
    span {
        font-size: 13px;
    }
    ${(props) =>
        props.$disabled &&
        `
        opacity: 0.5;
        pointer-events: none;
        cursor: default;
    `}
`;
const PageBlockLearnMore = styled.a`
    color: ${(props) => props.theme.app.component.link.color};
    text-decoration: ${(props) => props.theme.app.component.link.decoration};
    cursor: pointer;
    font-size: ${(props) => props.theme.app.component.pageBlocksContainer.pageBlock.pageBlockDescription.fontSize};
`;

const PageBlockSkeletonDescription = styled(SkeletonStack)`
    flex: 1;
    padding-top: 2px;
`;

export const PageBlocksListSkeleton: FC<PageBlocksListSkeletonProps> = ({ count = 6, columns = 5, showLearnMore = true }) => (
    <PageBlocksContainer $columns={columns} aria-hidden='true'>
        {Array.from({ length: count }).map((_, index) => (
            <PageBlock key={index}>
                <SkeletonBlock $width={index % 2 === 0 ? '150px' : '120px'} $height='18px' />
                <PageBlockSkeletonDescription $gap='8px' $margin='14px 0 18px'>
                    <SkeletonBlock $width='92%' $height='12px' />
                    <SkeletonBlock $width='78%' $height='12px' />
                    <SkeletonBlock $width='64%' $height='12px' />
                </PageBlockSkeletonDescription>
                <SkeletonRow $justify='space-between'>
                    <SkeletonBlock $width='80px' $height='18px' />
                    {showLearnMore && <SkeletonBlock $width='74px' $height='12px' />}
                </SkeletonRow>
            </PageBlock>
        ))}
    </PageBlocksContainer>
);

export const PageBlocksList: FC<PageBlocksListProps> = ({ blocks, columns = 5 }) => {
    const { isAllowed } = usePermissions();
    const navigate = useNavigate();
    const { hasPantoneColors } = usePantoneColorsCheck();
    const { openHelpGuide } = useInlineHelp();

    return (
        <PageBlocksContainer $columns={columns}>
            {blocks.map((block, idx) => {
                const isExternalUrl = block.link && block.link.startsWith('http');

                if (!hasPantoneColors && block.link?.toLowerCase().includes('pantone')) {
                    return null;
                }

                return (
                    <PageBlock key={idx.toString()}>
                        <PageBlockTitle>
                            <span>{block.label}</span> {block.isNew && <NewTag>New</NewTag>} {block.isComingSoon && <ComingSoonTag>Coming Soon</ComingSoonTag>}{' '}
                            {block.isEarlyAccess && <EarlyAccessTag>Early Access</EarlyAccessTag>}
                        </PageBlockTitle>
                        <PageBlockDescription dangerouslySetInnerHTML={{ __html: block.description }} />

                        <PageBlockFooter>
                            <StyledTextButton
                                $disabled={block.isComingSoon || !isAllowed(block.permissions)}
                                onClick={() => {
                                    if (block.isComingSoon || !isAllowed(block.permissions)) return;
                                    if (block.buttonAction) {
                                        block.buttonAction();
                                    } else if (block.link && isExternalUrl) {
                                        window.open(block.link, '_blank');
                                    } else if (block.link) {
                                        navigate(block.link);
                                    }
                                }}
                            >
                                {block.buttonLabel}
                            </StyledTextButton>{' '}
                            {block.learnMore && (
                                <PageBlockLearnMore
                                    href={block.learnMore}
                                    target={'_blank'}
                                    onClick={(event) => {
                                        if (!block.learnMore) return;

                                        event.preventDefault();
                                        openHelpGuide(block.learnMore);
                                    }}
                                >
                                    {T._('Learn more', 'Admin')}
                                </PageBlockLearnMore>
                            )}
                        </PageBlockFooter>
                    </PageBlock>
                );
            })}
        </PageBlocksContainer>
    );
};
